import { useApolloClient, useMutation } from '@apollo/client';
import { Container } from '@risk-smart/themed-cloudscape-components';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import TextContent from '@risk-smart/themed-cloudscape-components/text-content';
import Button from '@risksmart-app/components/src/button';
import { useFileUpdate } from '@risksmart-app/components/src/file/useFileUpdate';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import {
  InsertChildActionDocument,
  InsertObligationChangeAttestationOneDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { SwitchHorizontal02 } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import Loading from 'src/components/loading';
import SimpleRatingBadge from 'src/components/simple-rating-badge/SimpleRatingBadge';
import { PageLayout } from 'src/layouts';
import ActionModal from 'src/pages/actions/ActionModal';
import type { ActionFormFieldData } from 'src/pages/actions/update/forms/actionsSchema';
import { toLocalDate } from 'src/utils/dateUtils';
import { getFriendlyId } from 'src/utils/friendlyId';

import { useGetObligationChangeById } from '@/hooks/queries/obligation-change/useGetObligationChangeById';
import { evictField } from '@/utils/graphqlUtils';
import { obligationChangesRegisterUrl } from '@/utils/urls';

const ObligationChangeDetailPage: FC = () => {
  const { obligationChangeId } = useParams<{ obligationChangeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common', { keyPrefix: 'obligationChanges' });
  const { t: rt } = useTranslation(['ratings']);
  const statuses = rt('obligation_change_status', { returnObjects: true });
  const { user } = useRisksmartUser();
  const apolloClient = useApolloClient();
  const { updateFiles } = useFileUpdate();

  const { data, loading, refetch } = useGetObligationChangeById({
    queryArgs: { obligationChangeId: obligationChangeId! },
  });

  const [isActionModalOpen, setIsActionModalOpen] = useState(false);

  const [insertAttestation, { loading: insertAttestationLoading }] =
    useMutation(InsertObligationChangeAttestationOneDocument);
  const [insertAction, { loading: insertActionLoading }] = useMutation(
    InsertChildActionDocument
  );

  const record = data?.obligation_change?.[0];
  const isRead = record?.attestations.some((a) => a.UserId === user?.userId);

  const statusRating = statuses.find(
    (s) => s.value === (isRead ? 'read' : 'unread')
  );

  const handleMarkAsRead = async () => {
    if (!obligationChangeId) {
      return;
    }
    await insertAttestation({
      variables: {
        object: {
          ObligationChangeId: obligationChangeId,
          UserId: user!.userId,
        },
      },
    });
    evictField(apolloClient.cache, 'obligation_change');
    refetch();
  };

  const handleCreateAction = async (values: ActionFormFieldData) => {
    const { files } = values;
    const result = await insertAction({
      variables: {
        ...values,
        ParentId: obligationChangeId!,
        CustomAttributeData: values.CustomAttributeData || undefined,
        DepartmentTypeIds:
          values.departments?.map((d) => d.DepartmentTypeId) || [],
        TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
        ...ownerAndContributorIds(values),
      },
    });
    const actionId = result.data?.insertChildAction?.Id;
    if (!actionId) {
      throw new Error('Missing actionId');
    }
    await updateFiles({
      parentType: Parent_Type_Enum.Action,
      parentId: actionId,
      originalFiles: values?.files.filter(
        (f) => !(f instanceof File)
      ) as RelationFile[],
      selectedFiles: files,
    });
    evictField(apolloClient.cache, 'action');
    evictField(apolloClient.cache, 'obligation_change');
    setIsActionModalOpen(false);
    refetch();
  };

  const sequentialIdLabel = record
    ? `(${getFriendlyId('obligation_change', record.SequentialId)})`
    : '';

  return (
    <PageLayout
      helpTranslationKey={'obligationChanges.registerHelp'}
      title={record?.Title || t('detail_title')}
      counter={sequentialIdLabel}
      actions={
        <Button ariaLabel={'compare'} variant={'link'}>
          <SwitchHorizontal02 />
        </Button>
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <SpaceBetween size={'m'}>
          <div className={'flex gap-6'}>
            <Container className={'flex-1'}>
              <TextContent>
                <h3>{t('columns.description')}</h3>
                <p className={'whitespace-pre-wrap'}>{record?.Description}</p>
              </TextContent>
            </Container>

            <Container className={'min-w-[280px] max-w-[320px]'}>
              <SpaceBetween size={'s'}>
                <TextContent>
                  <h4>{t('columns.status')}</h4>
                  <SimpleRatingBadge rating={statusRating} />
                </TextContent>
                {record?.Reference && (
                  <TextContent>
                    <h4>{t('rule_number')}</h4>
                    <p>{record.Reference}</p>
                  </TextContent>
                )}
                {record?.Regulator && (
                  <TextContent>
                    <h4>{t('regulatory_body')}</h4>
                    <p>{record.Regulator}</p>
                  </TextContent>
                )}
                {record?.EffectiveDate && (
                  <TextContent>
                    <h4>{t('columns.effective_date')}</h4>
                    <p>{toLocalDate(record.EffectiveDate)}</p>
                  </TextContent>
                )}
              </SpaceBetween>
            </Container>
          </div>

          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              variant={'normal'}
              onClick={() => navigate(obligationChangesRegisterUrl())}
              disabled={insertActionLoading || insertAttestationLoading}
            >
              {t('cancel')}
            </Button>
            {!isRead && (
              <Button
                variant={'normal'}
                onClick={handleMarkAsRead}
                loading={insertActionLoading || insertAttestationLoading}
              >
                {t('mark_as_read')}
              </Button>
            )}
            <Button
              variant={'primary'}
              onClick={() => setIsActionModalOpen(true)}
              loading={insertActionLoading || insertAttestationLoading}
            >
              {t('create_action')}
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      )}

      {isActionModalOpen && (
        <ActionModal
          onSaving={handleCreateAction}
          onDismiss={() => setIsActionModalOpen(false)}
        />
      )}
    </PageLayout>
  );
};

export default ObligationChangeDetailPage;
